import { useEffect, useRef, useState } from "react"
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import { Each } from "../common/Each"
import useClickOutside from "../common/hooks/useClickOutside"
import styles from "./Select.module.css"
import TextInput from "./TextInput"

const Select = ({ selected, options, placeholder, style, onSelect = () => { }, nullable = false, theme, nullLabel = "Nessuna selezione", hideArrow = false, disabled = false, loading = false, wrapperRef, onSearch, searchPlaceholder }) => {

    const [value, setValue] = useState(selected)
    const [opts, setOpts] = useState(options)
    const [optionsOpen, setOptionsOpen] = useState(false)
    const [top, setTop] = useState(null)
    const containerRef = useRef(null)
    const ref = useRef(null)

    const handleClickOutside = (e) => {
        if(typeof e?.target?.className === "string" && e.target.className.includes('searchInput')) {
            return;
        }
        setOptionsOpen(false)
    }

    useClickOutside(containerRef, handleClickOutside)

    useEffect(() => {

        if (!containerRef.current) return;

        const computeAndSetPosition = () => {
            if (!(containerRef.current && ref.current)) return;

            const containerRect = containerRef.current.getBoundingClientRect();
            const wrapperEl = wrapperRef?.current;

            let openUp = false;
            if (wrapperEl) {
                const wrapperRect = wrapperEl.getBoundingClientRect();
                const distanceToBottom = wrapperRect.bottom - (containerRect.top + containerRect.height);
                const distanceToTop = containerRect.top - wrapperRect.top;

                if (distanceToBottom < 150) {
                    openUp = true;
                } else if (distanceToTop < 150) {
                    openUp = false; // explicitly open down near the top edge
                } else {
                    openUp = false; // default preference: open down
                }
            } else {
                const distanceToBottom = window.innerHeight - (containerRect.top + containerRect.height);
                const distanceToTop = containerRect.top;
                if (distanceToBottom < 150) {
                    openUp = true;
                } else if (distanceToTop < 150) {
                    openUp = false;
                } else {
                    openUp = false;
                }
            }

            if (openUp) {
                setTop(-ref.current.clientHeight - 2);
            } else {
                setTop(containerRef.current.clientHeight);
            }
        };

        const resizeObserver = new ResizeObserver(() => {
            computeAndSetPosition();
        });
        resizeObserver.observe(containerRef.current);

        // Also compute once immediately
        computeAndSetPosition();

        return () => resizeObserver.disconnect(); // clean up 
    }, [wrapperRef]);

    useEffect(() => {
        var opts = options
        if (nullable) {
            opts = [{ id: -1, name: nullLabel, value: null }, ...options]
        }
        setOpts(opts)
        setValue(selected ?? null)
    }, [options, nullable, nullLabel, selected])

    const handleOnSelect = async (value) => {
        setValue(value)
        onSelect(value)
    }

    useEffect(() => {
        setValue(selected)
    }, [selected])

    useEffect(() => {
        if (!containerRef?.current || !ref?.current) return;

        if (containerRef && ref && containerRef.current && ref.current) {
            setTimeout(() => {
                if (!(containerRef && ref && containerRef.current && ref.current)) return;

                const containerRect = containerRef.current.getBoundingClientRect();
                const wrapperEl = wrapperRef?.current;

                let openUp = false;
                if (wrapperEl) {
                    const wrapperRect = wrapperEl.getBoundingClientRect();
                    const distanceToBottom = wrapperRect.bottom - (containerRect.top + containerRect.height);
                    const distanceToTop = containerRect.top - wrapperRect.top;
                    if (distanceToBottom < 150) {
                        openUp = true;
                    } else if (distanceToTop < 150) {
                        openUp = false;
                    } else {
                        openUp = false;
                    }
                } else {
                    const distanceToBottom = window.innerHeight - (containerRect.top + containerRect.height);
                    const distanceToTop = containerRect.top;
                    if (distanceToBottom < 150) {
                        openUp = true;
                    } else if (distanceToTop < 150) {
                        openUp = false;
                    } else {
                        openUp = false;
                    }
                }

                if (openUp) {
                    setTop(-ref.current.clientHeight - 2);
                } else {
                    setTop(containerRef.current.clientHeight);
                }
            }, 200)
        }
    }, [options, wrapperRef, optionsOpen])

    return (
        <div
            style={{ ...style}}
            className={`${styles.container} ${optionsOpen ? styles.open : ''} ${theme === 'dark' ? styles.dark : ''} ${disabled ? styles.disabled : ''}`}
            ref={containerRef}
            onClick={(e) => {
                if ((options.length > 0 || onSearch) && !disabled ) {
                    if(typeof e.target?.className === "string" && e.target.className.includes('searchInput')) return;
                    setOptionsOpen(!optionsOpen)
                }
            }} >
            <div className={styles.selectedLabel}>
                {value &&
                    <>
                        {value.name}
                    </>
                }
                {!value &&
                    <>
                        {placeholder}
                    </>}
            </div>

            <div className={`${styles.options} ${((opts && opts.length > 0 && optionsOpen) || (optionsOpen && onSearch)) ? styles.open : ''}`} ref={ref} style={{ top: `${top + 4}px` }}>
                {onSearch && typeof onSearch === 'function' &&
                    <div className={styles.searchContainer}>
                        <TextInput id="select-search-input" type="search" placeholder={searchPlaceholder || "Cerca..."} className={styles.searchInput} onKeyUp={(e) => {
                            onSearch(e?.target?.value ?? "")
                        }} />
                    </div>
                }
                <Each of={opts} render={(item) => {
                    return (
                        <div className={`${styles.option} ${value?.id === item.id ? styles.selected : ''}`} onClick={() => {
                            handleOnSelect(item)
                        }}>
                            {item.name}
                        </div>
                    )
                }} />
            </div>

            {!hideArrow && !loading &&
                <ChevronIcon className={`${styles.chevron} ${optionsOpen ? styles.open : ''}`} />
            }
            {
                loading &&
                <div className="loader" style={{ height: "16px" }} />
            }
        </div>
    )

}

export default Select